import React, { useState } from 'react';
import { Container, Typography, TextField, Button, Paper, Box, Alert, IconButton, InputAdornment, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import logger from '../utils/logger';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isFreewheelUser, setIsFreewheelUser] = useState(false);
  const [expiredDialogOpen, setExpiredDialogOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Controlla se l'ultimo utente loggato era FreeWheel
  React.useEffect(() => {
    const freewheelFlag = localStorage.getItem('isFreewheelUser');
    setIsFreewheelUser(freewheelFlag === 'true');
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    try {
      logger.debug('Tentativo di login con:', formData.email);
      
      // Uso la funzione login dal contesto di autenticazione
      const response = await login(formData.email, formData.password);
      // 🔒 SICUREZZA: Non loggare token nella risposta
      logger.debug('Login completato con successo');
      
      // Reindirizza alla pagina originale o alla dashboard
      const from = location.state?.from?.pathname || '/dashboard';
      logger.debug('Reindirizzamento a:', from);
      navigate(from);
      
    } catch (err) {
      logger.error('Errore durante il login:', err);
      if (err.response) {
        logger.debug('Risposta di errore:', err.response.status);
        
        // Se l'account è scaduto, mostra il dialog invece del messaggio generico
        if (err.response.data.expired) {
          setExpiredDialogOpen(true);
          setError(null); // Non mostrare il messaggio generico
        } else {
          setError(err.response.data.msg || 'Credenziali non valide. Riprova.');
        }
      } else if (err.request) {
        logger.error('Nessuna risposta ricevuta dal server');
        setError('Il server non risponde. Verifica che il server sia in esecuzione su http://localhost:5000.');
      } else {
        logger.error('Errore durante la configurazione della richiesta:', err.message);
        setError('Errore durante la connessione. Riprova più tardi.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      bgcolor: 'white'
    }}>
      <Container maxWidth="sm" sx={{ m: 0 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          {isFreewheelUser && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Box
                component="img"
                src="https://d2gt4h1eeousrn.cloudfront.net/72654804/header-soNFiV/X4X8Lj2-600x600.webp"
                alt="FreeWheel Logo"
                sx={{ height: 80, maxWidth: '100%', objectFit: 'contain' }}
              />
            </Box>
          )}
          
          <Typography variant="h4" align="center" gutterBottom>
            Accedi al Gestionale
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? 'Accesso in corso...' : 'ACCEDI'}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2">
                <a href="/track-maintenance" target="_blank" rel="noopener noreferrer">
                  Sei un cliente? Verifica lo stato della tua manutenzione
                </a>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Dialog per account scaduto */}
      <Dialog
        open={expiredDialogOpen}
        onClose={() => setExpiredDialogOpen(false)}
        aria-labelledby="expired-dialog-title"
        aria-describedby="expired-dialog-description"
      >
        <DialogTitle id="expired-dialog-title">
          Abbonamento Scaduto
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="expired-dialog-description">
            Il tuo abbonamento è scaduto. Per continuare a utilizzare il servizio, è necessario rinnovare l'abbonamento.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpiredDialogOpen(false)} color="primary">
            Chiudi
          </Button>
          <Button onClick={() => {
            setExpiredDialogOpen(false);
            // TODO: Implementare la logica di rinnovo
            // navigate('/pricing');
          }} variant="contained" color="primary">
            Rinnova Ora
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;